<template>
  <div class="loc-picker" @keydown="onKeydown">
    <label v-if="showSearch" class="loc-picker__search">
      <svg class="loc-picker__search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-4-4" />
      </svg>
      <input
        ref="searchInput"
        type="text"
        class="loc-picker__input"
        :placeholder="searchPlaceholder"
        role="combobox"
        aria-controls="loc-picker-list"
        aria-expanded="true"
        :aria-activedescendant="activeOptionId"
        :value="rawQuery"
        @input="onQueryInput($event.target.value)"
      />
      <span v-if="hint" class="loc-picker__kbd">{{ hint }}</span>
    </label>

    <div
      id="loc-picker-list"
      ref="list"
      class="loc-picker__list"
      role="listbox"
      aria-label="Locations"
      tabindex="-1"
    >
      <div v-if="query && flatRows.length" class="loc-picker__label" role="presentation">
        {{ flatRows.length === 1 ? '1 location' : flatRows.length + ' locations' }}
      </div>
      <template v-for="section in sections">
        <div
          v-if="section.header"
          :key="'h-' + section.key"
          class="loc-picker__label loc-picker__label--sticky"
          role="presentation"
        >{{ section.header }}</div>
        <div
          v-for="row in section.rows"
          :key="section.key + '-' + row.group.id"
          :id="optionId(section.key, row.group.id)"
          class="loc-picker__row"
          :class="{ 'loc-picker__row--active': row.index === activeIndex }"
          role="option"
          :aria-selected="row.group.id === currentId ? 'true' : 'false'"
          @mouseenter="activeIndex = row.index"
          @click="choose(row.group)"
        >
          <span class="loc-picker__tile" aria-hidden="true">
            <img v-if="hasImage(row.group)" :src="row.group.img" alt="" />
            <template v-else>{{ initialsOf(row.group.name) }}</template>
          </span>
          <span class="loc-picker__text">
            <span class="loc-picker__name"><span
              v-for="(part, pi) in highlight(row.group.name)"
              :key="pi"
              :class="{ 'loc-picker__hit': part.hit }"
            >{{ part.text }}</span></span>
            <span v-if="locationLine(row.group)" class="loc-picker__city">{{ locationLine(row.group) }}</span>
          </span>
          <i v-if="row.group.id === currentId" class="el-icon-check loc-picker__check" aria-label="Current location"></i>
        </div>
      </template>
      <div v-if="!flatRows.length" class="loc-picker__empty">
        No locations match "{{ query }}". <a href="#" @click.prevent="clearQuery">Clear</a>
      </div>
    </div>
    <div class="sr-only" aria-live="polite">{{ flatRows.length }} locations</div>
  </div>
</template>

<script lang="ts">
export default { name: 'group-picker' }
</script>

<script setup lang="ts">
// Location list shared by the sidebar dropdown (fewer than 9 locations) and the search
// palette (9 or more, the folded sidebar, phones and Ctrl K). Styles are global, in
// src/assets/sass/dashboard/custom/_location-picker.scss.
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { GroupSummary } from '@/util/activeGroup'
import {
  SEARCH_THRESHOLD,
  rankGroups,
  sectionGroups,
  highlightRanges,
  locationLine,
} from '@/util/groupPickerFilter'

const props = defineProps({
  groups: { type: Array as () => GroupSummary[], required: true },
  currentId: { type: Number, default: null },
  recentIds: { type: Array as () => number[], default: () => [] },
  autofocus: { type: Boolean, default: false },
  // The palette always shows search; the dropdown only from SEARCH_THRESHOLD locations.
  alwaysSearch: { type: Boolean, default: false },
  // Keyboard hint shown at the right of the search field (empty on phones).
  hint: { type: String, default: '' },
})

const emit = defineEmits(['select', 'close'])

const PLACEHOLDER_IMG = 'https://console.offthecouch.io/img/placeholder.jpg'

const searchInput = ref<HTMLInputElement | null>(null)
const list = ref<HTMLElement | null>(null)
const rawQuery = ref('')
const query = ref('')
const activeIndex = ref(0)
let debounceTimer: ReturnType<typeof setTimeout> | null = null

const showSearch = computed(() => props.alwaysSearch || props.groups.length >= SEARCH_THRESHOLD)
const searchPlaceholder = computed(() =>
  props.groups.length >= SEARCH_THRESHOLD ? 'Search ' + props.groups.length + ' locations' : 'Search locations'
)

interface Row { group: GroupSummary; index: number }
interface Section { key: string; header: string | null; rows: Row[] }

// Recent only helps in long lists, so it shows only where search does.
const recentGroups = computed<GroupSummary[]>(() => {
  if (query.value || props.groups.length < SEARCH_THRESHOLD) return []
  const byId = new Map(props.groups.map(g => [g.id, g]))
  const out: GroupSummary[] = []
  for (const id of props.recentIds) {
    const g = byId.get(id)
    if (g && g.id !== props.currentId) out.push(g)
  }
  return out
})

const sections = computed<Section[]>(() => {
  let index = 0
  const toRows = (items: GroupSummary[]): Row[] => items.map(group => ({ group, index: index++ }))
  if (query.value) {
    return [{ key: 'q', header: null, rows: toRows(rankGroups(props.groups, query.value)) }]
  }
  const out: Section[] = []
  if (recentGroups.value.length) {
    out.push({ key: 'recent', header: 'Recent', rows: toRows(recentGroups.value) })
  }
  for (const s of sectionGroups(props.groups)) {
    if (s.header === null) {
      out.push({ key: 'all', header: recentGroups.value.length ? 'All locations' : null, rows: toRows(s.items) })
    } else {
      out.push({ key: 's-' + s.header, header: s.header, rows: toRows(s.items) })
    }
  }
  return out
})

const flatRows = computed(() => {
  const out: { key: string; row: Row }[] = []
  for (const s of sections.value) for (const row of s.rows) out.push({ key: s.key, row })
  return out
})

function optionId(sectionKey: string, id: number): string {
  return 'loc-opt-' + sectionKey + '-' + id
}

const activeOptionId = computed(() => {
  const entry = flatRows.value[activeIndex.value]
  return entry ? optionId(entry.key, entry.row.group.id) : undefined
})

watch(query, () => {
  activeIndex.value = 0
})

function onQueryInput(value: string): void {
  rawQuery.value = value
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    query.value = rawQuery.value
  }, 120)
}

function clearQuery(): void {
  if (debounceTimer) clearTimeout(debounceTimer)
  rawQuery.value = ''
  query.value = ''
  if (searchInput.value) searchInput.value.focus()
}

function choose(g: GroupSummary): void {
  emit('select', g)
}

async function scrollActiveIntoView(): Promise<void> {
  await nextTick()
  const id = activeOptionId.value
  const el = id ? document.getElementById(id) : null
  if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' })
}

// Apply the typed text now instead of waiting for the search debounce.
async function flushQuery(): Promise<void> {
  if (rawQuery.value === query.value) return
  if (debounceTimer) clearTimeout(debounceTimer)
  query.value = rawQuery.value
  activeIndex.value = 0
  await nextTick()
}

async function onKeydown(e: KeyboardEvent): Promise<void> {
  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
    e.preventDefault()
    await flushQuery()
    const count = flatRows.value.length
    if (!count) return
    const step = e.key === 'ArrowDown' ? 1 : -1
    activeIndex.value = (activeIndex.value + step + count) % count
    scrollActiveIntoView()
  } else if (e.key === 'Enter') {
    // Enter right after typing must act on what was typed, not on the list from before
    e.preventDefault()
    await flushQuery()
    const entry = flatRows.value[activeIndex.value]
    if (entry) choose(entry.row.group)
  } else if (e.key === 'Escape') {
    e.stopPropagation()
    emit('close')
  }
}

function hasImage(g: GroupSummary): boolean {
  return !!g.img && g.img !== PLACEHOLDER_IMG
}

function initialsOf(name: string): string {
  return (String(name || '').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w.charAt(0)).join('') || '?').toUpperCase()
}

function highlight(name: string) {
  return highlightRanges(name, query.value)
}

onMounted(async () => {
  await nextTick()
  if (props.autofocus && searchInput.value) searchInput.value.focus()
  else if (list.value) list.value.focus({ preventScroll: true })
})

onBeforeUnmount(() => {
  if (debounceTimer) clearTimeout(debounceTimer)
})
</script>
